// dependecies import ==>
import React, { useEffect, useState } from "react";
import Popover from "react-popover";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import moment from "moment";
// assets and scss import ==>
import "./obstetricCalculatorView.scss";
import infoIcon from "../../../../assets/info.png";
import closeIconRed from "../../../../assets/close_icon_red.svg";
// constant file import ==>
import VitalDatePickerInput from "../../../common/VitalDatePickerInput";
import VitalComponent from "../../../common/VitalComponent";
import { DEFAULT_OBSTETRIC_DETAILS } from "../../ERXConstants/constants";
import useDebounce from "../../common/debounce";

const ObstetricCalculatorView = (props) => {
  const {
    element,
    obstetricDetails,
    setObstetricDetails,
    handleUpdateAPICall,
  } = props;

  const [showLmpOnHover, setShowLmpOnHover] = useState(false);
  const [showEddOnHover, setShowEddOnHover] = useState(false);
  const [showEgaOnHover, setShowEgaOnHover] = useState(false);

  let cycleLength =
    +obstetricDetails?.cycleLength - DEFAULT_OBSTETRIC_DETAILS?.cycleLength;
  const debounceForObstetricDetails = useDebounce(obstetricDetails, 500);

  // Lifecycle Methods ===>

  useEffect(() => {
    if (debounceForObstetricDetails?.callObstetricDetailsApi) {
      handleUpdateAPICall();
    }
  }, [debounceForObstetricDetails]);

  // <===

  // Helper Methods ===>

  // function to get difference of dates in no. of days
  const getNoOfDays = (date) => {
    const date1 = new Date(date);
    const date2 = new Date();
    const diffTime = Math.abs(date2 - date1);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));   
  };

  // function to get no. of days from ega weeks and days
  const getNoOfDaysFromEga = (egaWeeks, egaDays) => {
    return +((+egaWeeks ? +egaWeeks : 0) * 7 + (+egaDays ? +egaDays : 0));
  };

  // function to get lmp date from ega data and cycle length
  const getLmpDateFromEgaAndCycleLength = (date, noOfDays, noOfCycleLength) => {
    return moment(date ? date : new Date())
      .add(-(noOfDays + noOfCycleLength), "d")
      .format("DD MMM, YYYY");
  };

  // function to get edd date from lmp date
  const getEddDateFromLmpDate = (lmpValue, noOfCycleLength) => {
    return moment(lmpValue)
      .add(280 + noOfCycleLength, "d")
      .format("DD MMM, YYYY");
  };

  // <===

  // Actions ===>

  const handleMouseOver = (setHoverState) => {
    setHoverState(true);
  };

  const handleMouseOut = (setHoverState) => {
    setHoverState(false);
  };

  const handleObstetricCalculatorNotes = (e) => {
    setObstetricDetails({
      ...obstetricDetails,
      note: e.target.value,
      callObstetricDetailsApi: true,
    });
  };

  // function to calculate obstetric calculator on change of lmp and edd date
  const handleDateChange = (date, title) => {
    let lmpValue =
        title === "lmpDate"
          ? moment(date).format("DD MMM, YYYY")
          : getLmpDateFromEgaAndCycleLength(date, 280, cycleLength),
      eddValue =
        title === "eddDate"
          ? moment(date).format("DD MMM, YYYY")
          : getEddDateFromLmpDate(date, cycleLength);

    const diffDays = getNoOfDays(lmpValue);
    let egaWeeks = parseInt(
        (diffDays +
          (DEFAULT_OBSTETRIC_DETAILS?.cycleLength -
            obstetricDetails?.cycleLength)) /
          7
      ),
      egaDays =
        (diffDays +
          (DEFAULT_OBSTETRIC_DETAILS?.cycleLength -
            obstetricDetails?.cycleLength)) %
        7;

    let obstetricData = {
      ...obstetricDetails,
      lmpDate: lmpValue,
      eddDate: eddValue,
      egaWeeks: egaWeeks > 0 ? egaWeeks : 0,
      egaDays: egaDays > 0 ? egaDays : 0,
    };
    setObstetricDetails({ ...obstetricData, callObstetricDetailsApi: true });
  };

  // function to calculate obstetric calculator on change of ega weeks and days
  const handleOnChangeEga = (e, title) => {
    let value =
      !e.target.value?.trim() && obstetricDetails?.lmpDate ? 0 : e.target.value;

    if (value?.toString().length > 1) {
      if (value?.toString().charAt(0) === "0") {
        value = value?.toString().substr(1, value?.toString().length - 1);
      } else {
        if (title === "egaDays") {
          value = value?.toString().substr(0, value?.toString().length - 1);
        }
      }
    }
    let egaDays = +obstetricDetails?.egaDays ? +obstetricDetails?.egaDays : 0;

    if (title === "egaDays") {
      if (+value >= 7) {
        egaDays = 0;
      } else if (+value < 0) {
        egaDays = 6;
      } else {
        egaDays = value;
      }
    }

    let egaWeeks = +obstetricDetails?.egaWeeks
      ? +obstetricDetails?.egaWeeks
      : 0;
    if (title === "egaDays") {
      if (+value === 7) {
        egaWeeks += 1;
      } else if (+value < 0) {
        egaWeeks -= 1;
      }
    } else if (title === "egaWeeks") {
      egaWeeks = value;
    }

    let noOfDays = getNoOfDaysFromEga(egaWeeks, egaDays);

    let lmpValue = getLmpDateFromEgaAndCycleLength("", noOfDays, cycleLength);

    let obstetricData = {
      ...obstetricDetails,
      lmpDate: lmpValue,
      eddDate: getEddDateFromLmpDate(lmpValue, cycleLength),
      egaWeeks: egaWeeks > 0 ? egaWeeks : 0,
      egaDays: egaDays > 0 ? egaDays : 0,
    };

    setObstetricDetails({ ...obstetricData, callObstetricDetailsApi: true });
  };

  // function to calculate obstetric calculator on change of cycle length
  const handleCycleLengthChange = (val) => {
    let cycleLengthValue = val - DEFAULT_OBSTETRIC_DETAILS?.cycleLength;
    let lmpValue = obstetricDetails?.lmpDate
      ? obstetricDetails?.lmpDate
      : moment().format("DD MMM, YYYY");
    const diffDays = getNoOfDays(lmpValue);
    let egaWeeks = parseInt(
        (diffDays + (DEFAULT_OBSTETRIC_DETAILS?.cycleLength - val)) / 7
      ),
      egaDays = (diffDays + (DEFAULT_OBSTETRIC_DETAILS?.cycleLength - val)) % 7;

    let obstetricData = {
      ...obstetricDetails,
      lmpDate: lmpValue,
      eddDate: getEddDateFromLmpDate(lmpValue, cycleLengthValue),
      egaWeeks: egaWeeks > 0 ? egaWeeks : 0,
      egaDays: egaDays > 0 ? egaDays : 0,
      cycleLength: val,
    };

    setObstetricDetails({ ...obstetricData, callObstetricDetailsApi: true });
  };

  const handleClearObstetricCalculator = () => {
    setObstetricDetails({
      ...JSON.parse(JSON.stringify(DEFAULT_OBSTETRIC_DETAILS)),
      callObstetricDetailsApi: true,
    });
  };

  // <===

  // Display UI ===>

  const displayInputHeader = (header, hoverMsg, hoverState, setHoverState) => {
    return (
      <div className="obstetric-calculator-input-header">
        <div>{header}</div>
        <Popover
          isOpen={hoverState}
          preferPlace={`end`}
          place={null}
          body={
            <span className="save-template-view-hover-text">{hoverMsg}</span>
          }
        >
          <img
            src={infoIcon}
            alt=""
            onMouseOver={() => handleMouseOver(setHoverState)}
            onMouseOut={() => handleMouseOut(setHoverState)}
          />
        </Popover>
      </div>
    );
  };

  const displayOCInputFields = () => {
    return (
      <div className="obstetric-lmp-edd-ega-wrapper">
        <div className="obstetric-lmp-edd-wrapper">
          <div className="obstetric-lmp-edd-inputs">
            {displayInputHeader(
              "LMP",
              "Last Menstrual Period",
              showLmpOnHover,
              setShowLmpOnHover
            )}
            <VitalDatePickerInput
              placeholder="Select Date"
              differentInputFormat={obstetricDetails?.lmpDate}
              selected_date={
                obstetricDetails?.lmpDate
                  ? new Date(obstetricDetails?.lmpDate)
                  : ""
              }
              maxDate={new Date()}
              allowBackspace={false}
              handleDateChange={(date) => handleDateChange(date, "lmpDate")}
            />
          </div>
          <div className="obstetric-lmp-edd-inputs">
            {displayInputHeader(
              "EDD",
              "Estimated Date Of Delivery",
              showEddOnHover,
              setShowEddOnHover
            )}
            <VitalDatePickerInput
              placeholder="Select Date"
              differentInputFormat={obstetricDetails?.eddDate}
              selected_date={
                obstetricDetails?.eddDate
                  ? new Date(obstetricDetails?.eddDate)
                  : ""
              }
              allowBackspace={false}
              handleDateChange={(date) => handleDateChange(date, "eddDate")}
            />
          </div>
        </div>
        <div className="obstetric-ega-wrapper">
          {displayInputHeader(
            "EGA",
            "Estimated Gestational Age",
            showEgaOnHover,
            setShowEgaOnHover
          )}
          <div className="obstetric-ega-wrapper-inputs">
            <div className="obstetric-ega-wrapper-inputs-ui">
              <VitalComponent
                value={obstetricDetails?.egaWeeks}
                step={1}
                selectedUnit="Weeks"
                handleOnChange={(e) => {
                  handleOnChangeEga(e, "egaWeeks");
                }}
              />
            </div>
            <div className="obstetric-ega-wrapper-inputs-ui">
              <VitalComponent
                value={obstetricDetails?.egaDays}
                step={1}
                selectedUnit="Days"
                handleOnChange={(e) => {
                  handleOnChangeEga(e, "egaDays");
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const displaySliderUI = () => {
    return (
      <div className="obstetric-calculator-slider-container">
        <div className="obstetric-calculator-slider-value">
          Cycle length:{" "}
          <span style={{ fontWeight: "500" }}>
            {obstetricDetails?.cycleLength} days
          </span>
        </div>
        <Box sx={{ width: window.innerWidth > 768 ? 242 : "100%" }}>
          <Slider
            value={obstetricDetails?.cycleLength}
            min={21}
            max={40}
            onChange={(e, newValue) => handleCycleLengthChange(newValue)}
          />
        </Box>
      </div>
    );
  };

  const displayNotes = () => {
    return (
      <div className="obstetric-calculator-view-notes">
        <div className="obstetric-calculator-view-notes-header">Note</div>
        <textarea
          maxLength={300}
          rows="3"
          value={obstetricDetails?.note}
          onChange={handleObstetricCalculatorNotes}
        ></textarea>
      </div>
    );
  };

  const displayClearObstetricDetailsUI = () => {
    return (
      <div className="obstetric-calculator-clear-btn-container">
        <div
          onClick={handleClearObstetricCalculator}
          className="obstetric-calculator-clear-btn"
        >
          <img alt="" src={closeIconRed} />
          Clear
        </div>
      </div>
    );
  };

  // <===

  return (
    <div id={element.name} className="obstetric-calculator-container">
      {displayOCInputFields()}
      {displaySliderUI()}
      {displayNotes()}
      {(obstetricDetails?.lmpDate?.trim() || obstetricDetails?.note?.trim()) &&
        displayClearObstetricDetailsUI()}
    </div>
  );
};

export default ObstetricCalculatorView;
